import { flow, pipe } from 'fp-ts/function';
import { array, nonEmptyArray, option, record } from 'fp-ts';
import { createSelector } from 'reselect';
import { createAction, createReducer } from '@mews/framework';
import { getStringProp } from '@mews/fp';
import { ApplicationState } from '@mews-commander/types';
import { createOptionsSelector, sortByName } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import prefix from '../prefix';
import moduleSelector from '../selector';
import { getEnterprisesByIds } from './enterprises';

export const NAME = 'enterpriseRelationships';

export interface EnterpriseRelationship {
    sourceEnterpriseId: string;
    targetEnterpriseId: string;
}

type EnterpriseRelationshipState = Record<string, Array<EnterpriseRelationship>>;

const initialState: EnterpriseRelationshipState = {};

const ENTERPRISE_RELATIONSHIPS_SET = prefix('ENTERPRISE_RELATIONSHIPS_SET');

export const setEnterpriseRelationships = (enterpriseRelationships: Array<EnterpriseRelationship>) =>
    createAction(ENTERPRISE_RELATIONSHIPS_SET, enterpriseRelationships);

export const enterpriseRelationshipsReducer = createReducer(initialState, {
    [ENTERPRISE_RELATIONSHIPS_SET]: (
        state: EnterpriseRelationshipState,
        enterpriseRelationships: Array<EnterpriseRelationship>
    ) =>
        array.isNonEmpty(enterpriseRelationships)
            ? {
                  ...state,
                  ...pipe(
                      enterpriseRelationships,
                      nonEmptyArray.groupBy(({ sourceEnterpriseId }) => sourceEnterpriseId)
                  ),
              }
            : state,
});

export const getEnterpriseRelationships = flow(moduleSelector, (state): EnterpriseRelationshipState => state[NAME]);

export const getEnterpriseRelationshipsBySourceId = createSelector(
    getStringProp('sourceEnterpriseId'),
    getEnterpriseRelationships,
    record.lookup<Array<EnterpriseRelationship>>
);

export const getTargetEnterpriseIdsBySourceId = createSelector(
    getEnterpriseRelationshipsBySourceId,
    option.map(array.map(({ targetEnterpriseId }) => targetEnterpriseId))
);

export const getCurrentTargetEnterpriseIds = (state: ApplicationState) =>
    pipe(
        state,
        getCurrentEnterpriseId,
        (currentEnterpriseId) => getTargetEnterpriseIdsBySourceId(state, { sourceEnterpriseId: currentEnterpriseId }),
        option.getOrElse(() => [] as string[])
    );

export const getCurrentTargetEnterprises = (state: ApplicationState) =>
    pipe(state, getCurrentTargetEnterpriseIds, (ids) => getEnterprisesByIds(state, { ids }));

export const getCurrentTargetEnterprisesOptions = createOptionsSelector(getCurrentTargetEnterprises, {
    sortFunction: sortByName,
});
